import React, { useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, XCircle } from 'lucide-react';

const PricingSection = () => {
  const scriptInitialized = useRef(false);

  // === URLs ===
  const DIY_URL =
    'https://www.mycreditcents.com/join';
  const MONTHLY_URL =
    'https://portal.thefinancialer.com/portal-signUp/signup.jsp?id=bnJ6dkdxVWNLbEV4blBiVGxGZGxUQT09';
  const SIX_URL =
    'https://portal.thefinancialer.com/portal-signUp/signup.jsp?id=MGprNmxyeEZiYUdQRmhBd2pDd3J6QT09';
  const ANNUAL_URL =
    'https://portal.thefinancialer.com/portal-signUp/signup.jsp?id=ems1TUxmK2VnY2hBNGM1N25ONmlyUT09';
  const FAST_TRACK_URL = ANNUAL_URL; // mismo destino

  const plans = [
    {
      name: "DIY Credit Repair",
      description: "For those who want to take control with expert guidance.",
      features: [
        { text: "Access to dispute letter templates", included: true },
        { text: "Step-by-step video tutorials", included: true },
        { text: "Credit monitoring tools", included: true },
        { text: "Community forum access", included: true },
        { text: "Personalized credit analysis", included: false },
        { text: "Dedicated credit coach", included: false },
        { text: "Debt negotiation support", included: false }
      ],
      buttonText: "Start DIY",
      price: "$39.99/month",
      ctaHref: DIY_URL
    },
    {
      name: "Full Rebuild Program",
      description: "Comprehensive support for rapid credit transformation.",
      features: [
        { text: "Done-For-You Credit Repair Letters (written, printed and mailed on your behalf)", included: true },
        { text: "Access to Step-By-Step Credit Training Program To Boost Credit Score", included: true },
        { text: "Credit monitoring tools", included: true },
        { text: "Community forum access", included: true },
        { text: "Personalized credit analysis", included: true },
        { text: "Dedicated credit coach", included: true },
        { text: "Debt negotiation support", included: true }
      ],
      buttonText: "Enroll Now",
      highlight: true,
    }
  ];

  useEffect(() => {
    if (scriptInitialized.current) return;
    scriptInitialized.current = true;

    setTimeout(() => {
      (function () {
        const monthlyBtn = document.getElementById('monthly-btn');
        const sixBtn = document.getElementById('six-btn');
        const annualBtn = document.getElementById('annual-btn');
        const priceDisplay = document.getElementById('price-display');
        const priceSuffix = priceDisplay ? priceDisplay.querySelector('.price-suffix') : null;
        const annualPriceText = document.getElementById('annual-price-text');
        const microCopy = document.getElementById('micro-copy');
        const bonusesSection = document.getElementById('bonuses-section');
        const primaryCta = document.getElementById('primary-cta');

        // === Forzar link del Fast-Track (texto: "Unlock Your Fast-Track Year") ===
        const norm = (s) =>
          (s || '')
            .replace(/\s+/g, ' ')
            .replace(/[–—-]/g, '-') // normaliza guiones
            .trim()
            .toLowerCase();

        const matchesFastTrack = (el) => {
          const t = norm(el.textContent);
          return (
            /unlock your fast-?track( year)?/i.test(t) ||
            t.includes('unlock your fast-track year') ||
            t.includes('unlock your fast track year') ||
            t === 'unlock your fast-track' ||
            t === 'unlock your fast track'
          );
        };

        const wireFastTrack = () => {
          let el =
            document.getElementById('fast-track-btn') ||
            document.querySelector('[data-fast-track="true"]');

          if (!el) {
            el = Array.from(document.querySelectorAll('a,button,[role="button"]'))
              .find(matchesFastTrack);
          }

          if (!el) {
            console.warn('Fast-Track no encontrado (busco "Unlock Your Fast-Track Year").');
            return;
          }

          if (el.tagName && el.tagName.toLowerCase() === 'a') {
            el.setAttribute('href', FAST_TRACK_URL);
            el.setAttribute('target', '_blank');
            el.setAttribute('rel', 'noopener noreferrer');
          } else {
            el.onclick = () => { window.location.href = FAST_TRACK_URL; };
          }
        };

        wireFastTrack();
        const mo = new MutationObserver(() => wireFastTrack());
        mo.observe(document.body, { childList: true, subtree: true });
        setTimeout(() => mo.disconnect(), 2000);

        // Validación mínima para el toggle
        if (!monthlyBtn || !sixBtn || !annualBtn || !priceDisplay || !annualPriceText || !microCopy || !bonusesSection || !primaryCta) {
          console.warn("Pricing toggle: elementos no listos.");
          scriptInitialized.current = false;
          return;
        }

        const prices = {
          monthly: {
            value: 139.99,
            suffix: '/month',
            microCopy: 'Start your credit rebuild today – flexible month-to-month commitment.',
            cta: { text: 'Start Monthly', href: 'https://portal.thefinancialer.com/portal-signUp/signup.jsp?id=bnJ6dkdxVWNLbEV4blBiVGxGZGxUQT09' }
          },
          six: {
            total: 659,
            perMonth: parseFloat((659 / 6).toFixed(2)),
            perDay: parseFloat((659 / 182.5).toFixed(2)),
            subtitle: '$3.61/day · $659 total · $180 savings · SmartCredit not included',
            microCopy: '6-month package: one payment of $659. $180 savings. SmartCredit not included.',
            cta: { text: 'Choose 6-Month', href: 'https://portal.thefinancialer.com/portal-signUp/signup.jsp?id=MGprNmxyeEZiYUdQRmhBd2pDd3J6QT09' }
          },
          annual: {
            value: 104.99,
            suffix: '/month',
            randomMessages: [
              { main: '$3.45/day', sub: 'If you can invest less than four bucks a day, you’re in. Consider yourself on board.' },
              { main: '$3.45/day', sub: 'No excuses.' },
              { main: '$3.45/day', sub: 'That’s your ticket.' },
              { main: '$3.45/day', sub: 'That’s all it takes.' },
              { main: '$3.45/day', sub: 'Easy call.' },
              { main: '$3.45/day', sub: 'Best Value, hands down.' },
              { main: '$3.45/day', sub: 'That’s pocket change.' },
              { main: '$3.45/day', sub: 'That’s cheaper than your coffee.' },
              { main: '$3.45/day', sub: 'All in.' },
              { main: '$3.45/day', sub: 'You can swing that.' }
            ],
            microCopy: 'Lock in your savings today and get $419 in bonuses – no monthly billing, just results.<br />Full year commitment.',
            cta: { text: 'Choose Annual', href: 'https://portal.thefinancialer.com/portal-signUp/signup.jsp?id=ems1TUxmK2VnY2hBNGM1N25ONmlyUT09' }
          }
        };

        let currentAnnualMessage =
          prices.annual.randomMessages[Math.floor(Math.random() * prices.annual.randomMessages.length)];
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        function activate(btn) {
          [monthlyBtn, sixBtn, annualBtn].forEach(b => b.classList.remove('active'));
          btn.classList.add('active');
          [monthlyBtn, sixBtn, annualBtn].forEach(b =>
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false')
          );
        }

        function updateCTA(mode) {
          if (mode === 'annual') {
            primaryCta.textContent = prices.annual.cta.text;
            primaryCta.href = prices.annual.cta.href;
          } else if (mode === 'six') {
            primaryCta.textContent = prices.six.cta.text;
            primaryCta.href = prices.six.cta.href;
          } else {
            primaryCta.textContent = prices.monthly.cta.text;
            primaryCta.href = prices.monthly.cta.href;
          }
        }

        function updatePlan(mode) {
          if (mode === 'monthly') activate(monthlyBtn);
          else if (mode === 'six') activate(sixBtn);
          else activate(annualBtn);

          let startPrice = 139.99;
          if (priceDisplay.textContent && priceDisplay.textContent.includes('/day')) {
            startPrice = parseFloat(priceDisplay.textContent.replace('$', '').replace('/day', ''));
          } else {
            const parsed = parseFloat((priceDisplay.textContent || '').replace('$', '').replace(',', ''));
            if (!isNaN(parsed)) startPrice = parsed;
          }

          if (!prefersReducedMotion) {
            const duration = 600;
            const startTime = performance.now();

            function animatePrice(t) {
              const p = Math.min((t - startTime) / duration, 1);

              if (mode === 'monthly') {
                const end = prices.monthly.value;
                const val = startPrice + (end - startPrice) * p;
                priceDisplay.textContent = '$' + val.toFixed(2);
                if (priceSuffix) priceSuffix.textContent = prices.monthly.suffix;
              } else if (mode === 'six') {
                const end = prices.six.perMonth;
                const val = startPrice + (end - startPrice) * p;
                priceDisplay.textContent = '$' + val.toFixed(2);
                if (priceSuffix) priceSuffix.textContent = '/month';
              } else {
                priceDisplay.textContent = currentAnnualMessage.main;
                if (priceSuffix) priceSuffix.textContent = '';
              }

              if (p < 1) requestAnimationFrame(animatePrice);
              else {
                if (mode === 'monthly') {
                  priceDisplay.textContent = '$' + prices.monthly.value.toFixed(2);
                  if (priceSuffix) priceSuffix.textContent = prices.monthly.suffix;
                } else if (mode === 'six') {
                  priceDisplay.textContent = '$' + prices.six.perMonth.toFixed(2);
                  if (priceSuffix) priceSuffix.textContent = '/month';
                } else {
                  priceDisplay.textContent = currentAnnualMessage.main;
                  if (priceSuffix) priceSuffix.textContent = '';
                }
              }
            }

            requestAnimationFrame(animatePrice);
          } else {
            if (mode === 'monthly') {
              priceDisplay.textContent = '$' + prices.monthly.value.toFixed(2);
              if (priceSuffix) priceSuffix.textContent = prices.monthly.suffix;
            } else if (mode === 'six') {
              priceDisplay.textContent = '$' + prices.six.perMonth.toFixed(2);
              if (priceSuffix) priceSuffix.textContent = '/month';
            } else {
              priceDisplay.textContent = currentAnnualMessage.main;
              if (priceSuffix) priceSuffix.textContent = '';
            }
          }

          microCopy.innerHTML =
            mode === 'annual'
              ? prices.annual.microCopy
              : mode === 'six'
              ? prices.six.microCopy
              : prices.monthly.microCopy;

          if (mode === 'annual') {
            annualPriceText.style.opacity = '0';
            annualPriceText.textContent = currentAnnualMessage.sub;
            setTimeout(() => (annualPriceText.style.opacity = '1'), prefersReducedMotion ? 0 : 50);
            annualPriceText.classList.add('visible');
          } else if (mode === 'six') {
            annualPriceText.style.opacity = '0';
            annualPriceText.textContent = prices.six.subtitle;
            setTimeout(() => (annualPriceText.style.opacity = '1'), prefersReducedMotion ? 0 : 50);
            annualPriceText.classList.add('visible');
          } else {
            annualPriceText.classList.remove('visible');
            annualPriceText.textContent = '';
          }

          if (mode === 'annual') bonusesSection.classList.add('visible');
          else bonusesSection.classList.remove('visible');

          updateCTA(mode);
        }

        if (!monthlyBtn.dataset.listenerAdded) {
          monthlyBtn.addEventListener('click', () => updatePlan('monthly'));
          monthlyBtn.dataset.listenerAdded = 'true';
        }
        if (!sixBtn.dataset.listenerAdded) {
          sixBtn.addEventListener('click', () => updatePlan('six'));
          sixBtn.dataset.listenerAdded = 'true';
        }
        if (!annualBtn.dataset.listenerAdded) {
          annualBtn.addEventListener('click', () => {
            currentAnnualMessage =
              prices.annual.randomMessages[Math.floor(Math.random() * prices.annual.randomMessages.length)];
            updatePlan('annual');
          });
          annualBtn.dataset.listenerAdded = 'true';
        }

        // Default
        updatePlan('monthly');
      })();
    }, 500);
  }, []);

  return (
    <motion.div
      initial={{ opacity: 0, y: 50 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      className="relative bg-gray-800/50 backdrop-blur-lg rounded-3xl p-8 md:p-12 shadow-2xl border border-gray-700/50 mb-12 text-center"
    >
      <h2 className="text-3xl md:text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-pink-400 to-red-600 mb-8">
        Choose Your Path to Financial Freedom
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {plans.map((plan, index) => (
          <div key={index} className="relative">
            {plan.highlight && (
              <motion.div
                className="absolute -inset-0.5 rounded-3xl z-0"
                style={{ background: 'linear-gradient(45deg, #ffffff, #f0f0f0, #ffffff)', filter: 'blur(4px)' }}
                animate={{ opacity: [0.2, 0.6, 0.2] }}
                transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
              />
            )}

            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.8 + index * 0.2 }}
              className={`relative z-10 p-8 rounded-3xl shadow-xl border ${plan.highlight ? 'bg-gradient-to-br from-purple-800 to-blue-800 border-purple-500' : 'bg-gray-900/50 border-gray-700'}`}
            >
              <h3 className="text-2xl font-bold mb-4 text-white">{plan.name}</h3>

              {plan.highlight ? (
                <div id="pricing-toggle-component">
                  <div className="toggle-container">
                    <div className="toggle-wrapper">
                      <button id="monthly-btn" className="toggle-button active" aria-pressed="true">
                        Monthly
                        <span id="most-popular-badge" className="tier-badge">MOST POPULAR</span>
                      </button>
                      <button id="six-btn" className="toggle-button" aria-pressed="false">
                        6-Month
                      </button>
                      <button id="annual-btn" className="toggle-button" aria-pressed="false">
                        Annual
                        <span id="best-value-badge" className="tier-badge gold">BEST VALUE</span>
                      </button>
                    </div>
                  </div>

                  <div className="price-container">
                    <p id="price-display" className="price-text">
                      $139.99<span className="price-suffix">/month</span>
                    </p>
                    <p id="annual-price-text" className="annual-text"></p>
                  </div>

                  <p id="micro-copy" className="micro-copy">
                    Start your credit rebuild today – flexible month-to-month commitment.
                  </p>

                  <div id="bonuses-section" className="bonuses-section">
                    <h4 className="bonuses-title">Additional Value with the 12-Month Plan</h4>
                    <ul className="bonuses-list">
                      <li className="bonus-item"><CheckCircle className="bonus-icon" /> Lifetime access to Rise Credit Training Program ($1,497 Value)</li>
                      <li className="bonus-item"><CheckCircle className="bonus-icon" /> Bonus #1: Credit Card Cheat Sheet ($99 Value)</li>
                      <li className="bonus-item"><CheckCircle className="bonus-icon" /> Bonus #2: Dealership Insider Secrets</li>
                      <li className="bonus-item"><CheckCircle className="bonus-icon" /> Bonus #3: Underground Credit Blueprint</li>
                      <li className="bonus-item"><CheckCircle className="bonus-icon" /> Bonus #4: The Truth About Credit Repair & Why Most Fail Guide</li>
                    </ul>
                  </div>

                  <a
                    id="primary-cta"
                    href={MONTHLY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full text-center mt-6 py-3 rounded-full font-bold text-lg transition-all duration-300 bg-white text-purple-800 shadow-lg hover:shadow-xl"
                  >
                    Start Monthly
                  </a>

                  <style jsx>{`
                    #pricing-toggle-component { font-family: inherit; color: #e2e8f0; text-align: center; padding-bottom: 1rem; }
                    .toggle-container { display: flex; justify-content: center; margin-bottom: 1.5rem; }
                    .toggle-wrapper { position: relative; display: flex; padding: 0.25rem; background-color: rgba(74,85,104,.7); border-radius: 9999px; box-shadow: inset 0 2px 4px rgba(0,0,0,.2); }
                    .toggle-button { flex: 1; padding: .5rem 1.5rem; font-size: .875rem; font-weight: 500; border-radius: 9999px; transition: all .3s ease; cursor: pointer; border: none; background: transparent; color: #a0aec0; outline: none; position: relative; }
                    .toggle-button:hover { color: #fff; }
                    .toggle-button.active { background-color: #8b5cf6; color: #fff; box-shadow: 0 1px 3px rgba(0,0,0,.1); }

                    /* Badges SIEMPRE visibles */
                    .tier-badge { 
                      position: absolute; 
                      top: -.75rem; 
                      left: 50%; 
                      transform: translateX(-50%) scale(1); 
                      transform-origin: center bottom; 
                      background-color: rgba(147,197,253,.25);
                      color: #bfdbfe; 
                      padding: .1rem .5rem; 
                      border-radius: 9999px; 
                      font-size: .6rem; 
                      font-weight: 700; 
                      text-transform: uppercase; 
                      white-space: nowrap; 
                      opacity: 1; 
                      pointer-events: none; 
                      animation: neon-glow 3s ease-in-out infinite alternate; 
                      box-shadow: 0 0 5px rgba(255,255,255,.5), 0 0 10px rgba(255,255,255,.3); 
                    }
                    .tier-badge.gold { 
                      background-color: rgba(255,215,0,.2); 
                      color: #ffd700; 
                    }
                    @keyframes neon-glow { 
                      from { box-shadow: 0 0 5px rgba(255,255,255,.5), 0 0 10px rgba(255,255,255,.3);} 
                      to { box-shadow: 0 0 10px rgba(255,255,255,.8), 0 0 20px rgba(255,255,255,.6), 0 0 30px rgba(255,255,255,.4);} 
                    }

                    .price-container { margin-bottom: 1rem; }
                    .price-text { font-size: 2.25rem; line-height: 1.25; font-weight: 800; color: #fff; margin-bottom: .5rem; }
                    @media (min-width: 640px) { .price-text { font-size: 3rem; } }
                    .annual-text { font-size: 1.25rem; font-weight: 600; color: #fff; opacity: 0; transition: opacity .3s ease; height: 0; overflow: hidden; }
                    .annual-text.visible { opacity: 1; height: auto; margin-bottom: .5rem; }
                    .micro-copy { color: #a0aec0; margin-bottom: 1.5rem; min-height: 2.5rem; }
                    .bonuses-section { max-height: 0; opacity: 0; overflow: hidden; transition: all .5s ease; margin-top: 0; padding-top: 0; border-top: 1px solid rgba(255,255,255,.1); text-align: left; }
                    .bonuses-section.visible { max-height: 500px; opacity: 1; margin-top: 1.5rem; padding-top: 1.5rem; }
                    .bonuses-title { font-size: 1.125rem; font-weight: 700; color: #fff; margin-bottom: 1rem; text-align: center; }
                    .bonuses-list { list-style: none; padding: 0; margin: 0; }
                    .bonus-item { display: flex; align-items: flex-start; margin-bottom: .75rem; color: #e2e8f0; }
                    .bonus-icon { flex-shrink: 0; margin-right: .75rem; width: 1.25rem; height: 1.25rem; }

                    @media (prefers-reduced-motion: reduce) {
                      .toggle-button, .annual-text, .bonuses-section, .tier-badge { transition: none !important; animation: none !important; }
                    }
                  `}</style>
                </div>
              ) : (
                <>
                  <p className="text-4xl sm:text-5xl font-extrabold text-white mb-4 leading-tight text-center">
                    {plan.price}
                  </p>
                  <a
                    href={plan.ctaHref}
                    className="block w-full text-center py-3 rounded-full font-bold text-lg transition-all duration-300 bg-purple-600 text-white shadow-lg hover:shadow-xl"
                    target="_blank" rel="noopener noreferrer"
                  >
                    {plan.buttonText}
                  </a>
                </>
              )}

              <p className="text-gray-300 mb-6">{plan.description}</p>
              <ul className="text-left space-y-3 mb-8">
                {plan.features.map((feature, fIndex) => (
                  <li key={fIndex} className="flex items-center text-gray-200">
                    {feature.included ? (
                      <CheckCircle className="text-green-400 mr-3 flex-shrink-0" size={20} />
                    ) : (
                      <XCircle className="text-red-400 mr-3 flex-shrink-0" size={20} />
                    )}
                    {feature.text}
                  </li>
                ))}
              </ul>
              {!plan.highlight && null}
            </motion.div>
          </div>
        ))}
      </div>
    </motion.div>
  );
};

export default PricingSection;
